'use client';
import './rte-editor.css';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Youtube from '@tiptap/extension-youtube';
import { Separator, useFloatingTriggerVisibility } from '@ui/components/ui';
import { useEffect, useMemo, useState } from 'react';
import { Avatar, Button } from '@ui/components/atoms';
import Placeholder from '@tiptap/extension-placeholder';
import { ContextUserType } from '@ui/constants/context';
import { FilePreview } from '@ui/components/moleculas';
import { IFile } from '@ui/hooks/file/types';
import { sendGAEvent } from '@next/third-parties/google';
import { twMerge } from 'tailwind-merge';
import {
  Link,
  HorizontalRule,
  CodeBlockLowlight,
  Selection,
  Color,
  UnsetAllMarks,
  Instagram,
  Twitter,
  Video,
} from './extensions';
import { TextStyle } from '@tiptap/extension-text-style';
import { Typography } from '@tiptap/extension-typography';
import Mention from '@tiptap/extension-mention';
import Suggestion from './extensions/mentions/suggestion';
import { useMobile, useScoreAnimation } from '@ui/hooks';
import AnimatedScoreIncrease from '@ui/components/organisms/engagement-score/components/animated-score-increase';
import { cn } from '@ui/lib/utils';
import CharacterCount from '@tiptap/extension-character-count';
import { CharacterCounterProgress } from './extensions/character-counter/character-counter-progress';
import {
  RichTextEditorToolbarList,
  RichTextEditorLinkDialog,
  RichTextEditorInstagramDialog,
  RichTextEditorYouTubeDialog,
  RichTextEditorTwitterDialog,
  // RichTextEditorVideoDialog,
} from './_component/rte-editor-toolbar-list';
import { useInteractionGuard } from '@hooks/index';

interface ICommonProps {
  files: IFile[];
  exceededFileLimit: boolean;
  hideFileUpload?: boolean;
  onSelectFiles?: (fileList: FileList) => void;
  allowVideos?: boolean;
}

interface RichTextEditorProps extends ICommonProps {
  characterLimit?: number;
  buttonLabel?: string;
  placeholder: string;
  editorLabel: string;
  creatorDisplayName?: string;
  authorAvatarURL?: string;
  authorAvatarFallbackName?: string;
  value: string;
  userType: ContextUserType;
  currentUser?: ContextUserType;
  isSubmitButtonDisabled: boolean;
  editorOnly?: boolean;
  isLoading?: boolean;
  maxFiles: number;
  activateToolbarOnFocus?: boolean;
  showCancelButton: boolean;
  isFocused?: boolean;
  enableSuggestions?: boolean;
  initialMention?: {
    id: string;
    label: string;
  };
  editorClassName?: string;
  showSaveDraftButton?: boolean;
  sendEmail?: boolean;
  deleteFile?: (id: string) => void;
  onChange: (
    value: string,
    mentions: string[],
    isSaveDraft: boolean,
    clearContent: () => void,
  ) => void;
  onEditorChange?: (value: string, editor: Editor) => void;
  onCancel: () => void;
  onToggleSendEmail?: (value: boolean) => void;
}

export const RichTextEditor = ({
  placeholder,
  editorLabel,
  buttonLabel,
  onCancel,
  enableSuggestions,
  ...rest
}: RichTextEditorProps) => {
  const { setFloatingTriggerVisibility } = useFloatingTriggerVisibility();
  const [content, setContent] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const { interceptInteraction } = useInteractionGuard();
  const { scoreAnimation, triggerScoreAnimation } = useScoreAnimation();
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showInstagramDialog, setShowInstagramDialog] = useState(false);
  const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
  const [showTwitterDialog, setShowTwitterDialog] = useState(false);
  // const [showVideoDialog, setShowVideoDialog] = useState(false);

  const joinMessage = useMemo(() => {
    return rest.creatorDisplayName
      ? `Join community to use this feature. Collect this badge to join community!`
      : 'Join this KOR Space to use this feature. Collect this badge to join community.';
  }, [rest.creatorDisplayName]);

  const editor = useEditor({
    immediatelyRender: true,
    editorProps: {
      attributes: {
        autocomplete: 'off',
        autocorrect: 'off',
        autocapitalize: 'off',
        class: twMerge(
          'max-h-[600px] w-full bg-transparent text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed overflow-auto',
          rest.editorClassName,
        ),
        ...(useMobile('(max-width: 500px)') && {
          style: 'font-size: 16px; -webkit-text-size-adjust: 100%;',
        }),
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        codeBlock: false,
        paragraph: { HTMLAttributes: { class: 'text-node' } },
        heading: { HTMLAttributes: { class: 'heading-node' } },
        blockquote: { HTMLAttributes: { class: 'block-node' } },
        orderedList: { HTMLAttributes: { class: 'list-node list-decimal pl-4' } },
        bulletList: { HTMLAttributes: { class: 'list-node list-disc pl-4' } },
        code: { HTMLAttributes: { class: 'inline', spellcheck: 'false' } },
        dropcursor: { width: 2, class: 'ProseMirror-dropcursor border' },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
        isAllowedUri: (url, ctx) => {
          try {
            // construct URL
            const parsedUrl = url.includes(':')
              ? new URL(url)
              : new URL(`${ctx.defaultProtocol}://${url}`);

            // use default validation
            if (!ctx.defaultValidate(parsedUrl.href)) {
              return false;
            }

            // disallowed protocols
            const disallowedProtocols = ['ftp', 'file', 'mailto'];
            const protocol = parsedUrl.protocol.replace(':', '');

            if (disallowedProtocols.includes(protocol)) {
              return false;
            }

            // only allow protocols specified in ctx.protocols
            const allowedProtocols = ctx.protocols.map((p) =>
              typeof p === 'string' ? p : p.scheme,
            );

            if (!allowedProtocols.includes(protocol)) {
              return false;
            }

            // disallowed domains
            const disallowedDomains = ['example-phishing.com', 'malicious-site.net'];
            const domain = parsedUrl.hostname;

            if (disallowedDomains.includes(domain)) {
              return false;
            }

            // all checks have passed
            return true;
          } catch {
            return false;
          }
        },
        shouldAutoLink: (url) => {
          try {
            // construct URL
            const parsedUrl = url.includes(':') ? new URL(url) : new URL(`https://${url}`);

            // only auto-link if the domain is not in the disallowed list
            const disallowedDomains = ['example-no-autolink.com', 'another-no-autolink.com'];
            const domain = parsedUrl.hostname;

            return !disallowedDomains.includes(domain);
          } catch {
            return false;
          }
        },
      }),
      Color,
      TextStyle,
      Selection,
      Typography,
      UnsetAllMarks,
      HorizontalRule,
      CodeBlockLowlight,
      Placeholder.configure({ placeholder: () => placeholder }),
      ...(enableSuggestions
        ? [
            Mention.configure({
              HTMLAttributes: {
                class: 'text-primary font-semibold',
              },
              suggestion: enableSuggestions ? Suggestion : undefined,
            }).extend({
              addOptions() {
                return {
                  ...this.parent?.(),
                  userType: rest.userType,
                };
              },
            }),
          ]
        : []),
      Instagram.configure({
        HTMLAttributes: {
          class: 'w-fit',
        },
        inline: false,
        addPasteHandler: true,
      }),
      Twitter.configure({
        HTMLAttributes: {
          class: 'not-prose',
        },
        inline: false,
        addPasteHandler: true,
      }),
      Youtube.configure({
        HTMLAttributes: {
          class: 'w-full max-w-full',
        },
        inline: false,
        addPasteHandler: true,
        width: 640,
        height: 360,
      }),
      Video.configure({
        HTMLAttributes: {
          class: 'w-full max-w-full',
        },
        inline: false,
      }),
      CharacterCount.configure({
        limit: rest.characterLimit,
      }),
    ],
    content: rest.value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
      if (rest.onEditorChange) {
        rest.onEditorChange(html, editor);
      }
    },
  });

  function getAllNodesAttributesByType(doc: any, nodeType: string) {
    const result: string[] = [];

    doc.descendants((node: any) => {
      if (node.type.name === nodeType) {
        result.push(node.attrs.id);
      }
    });

    return result;
  }

  const submit = interceptInteraction((isSaveDraft: boolean) => {
    const mentions = getAllNodesAttributesByType(editor.state.doc, 'mention');
    rest.onChange(content, mentions, isSaveDraft, () => {
      editor.commands.clearContent();
      setContent('');
    });
  }, joinMessage);

  const onDismissEditorContent = () => {
    editor.commands.clearContent();
    setContent('');
    onCancel();
    setIsFocused(false);
    setFloatingTriggerVisibility(true);
  };

  const handleSubmit = (isSaveDraft: boolean = false) => {
    if (buttonLabel === 'Post') {
      sendGAEvent('event', 'PostButtonClicked', {
        event_label: 'Post Button clicked',
      });
    } else if (buttonLabel === 'Comment') {
      sendGAEvent('event', 'CommentButtonClicked', {
        event_label: 'Comment Button clicked',
      });
    }

    if (!isSaveDraft) {
      triggerScoreAnimation(2, 'comment');
    }

    submit(isSaveDraft);
  };

  const FilesRender = useMemo(() => {
    return rest.files.map((file) => {
      return (
        <FilePreview
          className="h-14 w-14"
          key={file.id}
          file={file}
          onDelete={() => {
            if (rest.deleteFile) {
              rest.deleteFile(file.id);
            }
          }}
        />
      );
    });
  }, [rest.files, rest.deleteFile]);

  // Separate images from videos for submission
  const { imageFiles, videoFiles } = useMemo(() => {
    const images: typeof rest.files = [];
    const videos: typeof rest.files = [];

    rest.files.forEach((file) => {
      const isVideo = file.meta?.mime_type?.startsWith('video/');
      if (isVideo) {
        videos.push(file);
      } else {
        images.push(file);
      }
    });

    return { imageFiles: images, videoFiles: videos };
  }, [rest.files]);

  const isEditorOnlyCss = rest.editorOnly ? 'mt-0 ml-0 pl-0' : '';

  useEffect(() => {
    setContent(rest.value);
    const editorText = editor.getText();
    if (Boolean(rest.value) && !Boolean(editorText)) {
      editor.chain().focus().insertContent('\n\n\n').insertContent(rest.value).run();
    }

    return () => {
      const editorText = editor.getText();
      if (editor && !Boolean(editorText)) {
        editor.destroy();
      }
    };
  }, [rest.value, editor]);

  useEffect(() => {
    if (rest?.initialMention && editor) {
      const existingMentions = getAllNodesAttributesByType(editor.state.doc, 'mention');
      if (!existingMentions.includes(rest.initialMention.id)) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'mention',
            attrs: rest.initialMention,
            text: rest.initialMention.label,
          })
          .insertContent(' ')
          .run();
      }
    }
  }, [editor]);

  const isButtonDisabled = useMemo(() => {
    return !Boolean(content) || content?.length < 8 || rest.isSubmitButtonDisabled;
  }, [rest.isSubmitButtonDisabled, rest.isLoading, rest.exceededFileLimit, content]);

  const isMobile = useMobile('(max-width: 500px)');

  const handleLinkClick = () => {
    setShowLinkDialog(true);
  };

  const handleInstagramClick = () => {
    setShowInstagramDialog(true);
  };

  const handleYouTubeClick = () => {
    setShowYouTubeDialog(true);
  };

  const handleTwitterClick = () => {
    setShowTwitterDialog(true);
  };

  // const handleVideoClick = () => {
  //   setShowVideoDialog(true);
  // };

  // const handleUploadVideo = async (file: File): Promise<string> => {
  //   try {
  //     // Create FormData for video upload
  //     const formData = new FormData();
  //     formData.append('file', file);

  //     // Upload video using the same endpoint as images
  //     // The backend should handle video files based on MIME type
  //     const response = await fetch('/api/video-upload', {
  //       method: 'POST',
  //       body: formData,
  //       credentials: 'include',
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to upload video');
  //     }

  //     const data = await response.json();
  //     return data.location || data.url;
  //   } catch (error) {
  //     console.error('Error uploading video:', error);
  //     throw error;
  //   }
  // };

  return (
    <div className="bg-input h-fit rounded-md">
      <div className="flex flex-col gap-1 py-3 pl-4">
        {!rest.editorOnly && (
          <div className="mt-2 flex items-center gap-2">
            {rest.authorAvatarURL && (
              <Avatar size="s" src={rest.authorAvatarURL} type={rest.userType} />
            )}
            {rest.authorAvatarFallbackName && !rest.authorAvatarURL && (
              <Avatar
                size="s"
                src={rest.authorAvatarURL}
                type={rest.userType}
                displayName={rest.authorAvatarFallbackName}
              />
            )}
            {editorLabel && (
              <p className="text-md font-nunito font-semibold truncate">{editorLabel}</p>
            )}
          </div>
        )}
        <EditorContent
          editor={editor}
          className={twMerge(
            'editor-content z-10 -mt-1 ml-3 border-none bg-transparent pl-8',
            isEditorOnlyCss,
            !editorLabel ? 'ml-0 pl-0' : '',
          )}
          placeholder={placeholder}
          onFocus={() => {
            setIsFocused(true);
            setFloatingTriggerVisibility(false);
          }}
        />
      </div>

      {rest.files.length > 0 && (
        <div className="flex w-full flex-row gap-2 overflow-x-auto p-4">{FilesRender}</div>
      )}

      <div className="bg-korLightOrange/50 rounded-bl-md rounded-br-md">
        {editor && (
          <div
            className=" flex flex-row items-center justify-between px-2 py-1"
            style={{
              display: rest.activateToolbarOnFocus ? (isFocused ? 'flex' : 'none') : 'flex',
            }}>
            <div className="flex flex-row items-center p-1">
              {RichTextEditorToolbarList({
                ...rest,
                editor,
                minimized: isMobile,
                onLinkClick: handleLinkClick,
                onInstagramClick: handleInstagramClick,
                onYoutubeClick: handleYouTubeClick,
                onTwitterClick: handleTwitterClick,
                // onVideoClick: handleVideoClick,
              }).map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </div>
            <div className="flex gap-2">
              {rest.showCancelButton && (
                <Button
                  onClick={onDismissEditorContent}
                  type="secondary"
                  size="small"
                  className="h-8 w-fit min-w-fit">
                  Cancel
                </Button>
              )}
              {rest.showSaveDraftButton && (
                <Button
                  onClick={() => handleSubmit(true)}
                  isLoading={rest.isLoading}
                  type="outline"
                  size="small"
                  className="h-8 w-fit min-w-fit">
                  Save Draft
                </Button>
              )}
              <div className="relative">
                <AnimatedScoreIncrease
                  className={cn(
                    'absolute right-0 text-green-500',
                    scoreAnimation.position === 'comment' && 'top-[-25px]',
                  )}
                  scoreAnimation={scoreAnimation}
                  visiblePositions={['comment']}
                  userType={rest.currentUser!}
                />
                {buttonLabel && (
                  <Button
                    onClick={() => handleSubmit(false)}
                    isLoading={rest.isLoading}
                    type="primary"
                    size="small"
                    className="h-8 w-fit min-w-fit"
                    disabled={isButtonDisabled}>
                    {buttonLabel}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {rest.characterLimit && <Separator className="bg-gray-300" />}

        {rest.characterLimit && (
          <CharacterCounterProgress editor={editor} characterLimit={rest.characterLimit} />
        )}
      </div>

      <RichTextEditorLinkDialog
        editor={editor}
        showLinkDialog={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
      />
      <RichTextEditorInstagramDialog
        editor={editor}
        showInstagramDialog={showInstagramDialog}
        onClose={() => setShowInstagramDialog(false)}
      />
      <RichTextEditorYouTubeDialog
        editor={editor}
        showYouTubeDialog={showYouTubeDialog}
        onClose={() => setShowYouTubeDialog(false)}
      />
      <RichTextEditorTwitterDialog
        editor={editor}
        showTwitterDialog={showTwitterDialog}
        onClose={() => setShowTwitterDialog(false)}
      />
      {/* <RichTextEditorVideoDialog
        editor={editor}
        showVideoDialog={showVideoDialog}
        onClose={() => setShowVideoDialog(false)}
        onUploadVideo={handleUploadVideo}
      /> */}
    </div>
  );
};
